import Markdown from "react-markdown";
import { ChatMessageType } from "../../../types/chatWidget";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { readableTextColor } from "../../utils";

// Single dollar signs are left alone, or a reply like "$5 or $10" would turn into a formula
const REMARK_PLUGINS: any[] = [remarkGfm, [remarkMath, { singleDollarTextMath: false }]];
// MathML needs no stylesheet, and KaTeX's own can't reach inside the widget's shadow root
const REHYPE_PLUGINS: any[] = [[rehypeKatex, { output: "mathml" }]];
const MARKDOWN_COMPONENTS = {
  // Following a link shouldn't take visitors away from the page they're chatting on
  a: ({ node, ...props }: any) => <a {...props} target="_blank" rel="noopener noreferrer nofollow" />,
};

export default function ChatMessage({
  message,
  isSend,
  error,
  sender_name,
  isAgent,
  agent_name,
  user_message_style,
  bot_message_style,
  error_message_style,
  brand_color,
  onRetry,
}: ChatMessageType) {
  const agentColor = brand_color || "#2563eb";

  // Human-agent messages get a distinctive badge and left-accent border
  const displayName = isAgent
    ? (agent_name || sender_name || "Agent")
    : sender_name;

  return (
    <div
      className={
        "cl-chat-message " + (isSend ? " cl-justify-end" : " cl-justify-start")
      }
    >
      {isSend ? (
        <div style={user_message_style} className="cl-user_message">
          {message}
        </div>
      ) : error ? (
        <div style={error_message_style} className={"cl-error_message"} role="alert">
          {message}
          {onRetry && (
            <button type="button" className="cl-retry" onClick={onRetry}>
              Retry
            </button>
          )}
        </div>
      ) : (
        <div className="cl-bot_message_group">
          {/* Sender label: agent name (human) or bot name */}
          {!isSend && displayName && displayName !== "Machine" && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: "11px",
              marginBottom: "4px",
              marginLeft: isAgent ? "2px" : "4px",
              color: isAgent ? agentColor : "#6b7280",
              fontWeight: 600,
            }}>
              {isAgent && (
                <span style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: agentColor,
                  color: readableTextColor(agentColor),
                  fontSize: 9,
                  flexShrink: 0,
                }}>
                  {/* Person icon (inline SVG so no extra dep) */}
                  <svg viewBox="0 0 16 16" fill="currentColor" width={10} height={10}>
                    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-5 5a5 5 0 0 1 10 0H3Z" />
                  </svg>
                </span>
              )}
              {displayName}
            </div>
          )}
          <div
            style={{
              ...(isAgent
                ? {
                  borderLeft: `3px solid ${agentColor}`,
                  paddingLeft: 10,
                  borderRadius: "0 12px 12px 0",
                }
                : {}),
              ...bot_message_style,
            }}
            className={"cl-bot_message"}
          >
            <div className={"markdown-body prose flex flex-col word-break-break-word"}>
              <Markdown
                remarkPlugins={REMARK_PLUGINS}
                rehypePlugins={REHYPE_PLUGINS}
                components={MARKDOWN_COMPONENTS}
              >
                {message}
              </Markdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
